import { format } from "date-fns";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Skeleton } from "primereact/skeleton";
import { useState } from "react";
import AdminOnly from "~/components/AdminOnly";
import CustomeButton from "~/components/Button";
import Menu from "~/components/menu/Menu";
import {
  AppointmentHistoryTableProps,
  MappedAppointments,
} from "~/Pages/Directors/types/director";

const AppointmentHistoryTable = ({
  appointments,
  addAppointmentModalHandler,
  setEditAppointmentEntry,
  setIsEditAppointment,
  loading,
  isEditMode = false,
  deleteAppointmentModalHandler,
  hideActionBar = false,
}: AppointmentHistoryTableProps) => {
  const skeletonRows = [...Array(5)];
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  const onPageChange = (event: any) => {
    setFirst(event.first);
    setRows(event.rows);
  };

  const statusTemplate = (rowData: MappedAppointments) => {
    let className = "";
    switch (rowData?.status) {
      case "Active":
        className = "active-circle-fill";
        break;
      case "Removed":
        className = "removed-circle-fill";
        break;
      case "Not Active":
        className = "not-active-circle-fill";
        break;
      default:
        className = "not-active-circle-fill";
    }
    return loading ? (
      <Skeleton width="100%" />
    ) : (
      <div className="flex gap-1 align-items-center">
        <i className={`pi pi-circle-fill ${className}`} />
        {rowData?.status}
      </div>
    );
  };

  const actionMenuItems = (rowData: MappedAppointments) => [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: () => {
        setIsEditAppointment(true);
        setEditAppointmentEntry(rowData);
        addAppointmentModalHandler();
      },
    },
    // in edit mode show delete button
    ...(isEditMode
      ? [
          {
            label: "Delete",
            icon: "pi pi-trash",
            command: () => {
              setEditAppointmentEntry(rowData);
              deleteAppointmentModalHandler?.();
            },
          },
        ]
      : []),
  ];

  return (
    <div className="appointment-table-section flex flex-column gap-4">
      {!hideActionBar && (
        <AdminOnly>
          <div className="flex justify-content-end">
            <CustomeButton
              label="Add New"
              icon="pi pi-plus"
              iconPos="right"
              variant="contained"
              type="button"
              onClick={addAppointmentModalHandler}
            />
          </div>
        </AdminOnly>
      )}

      <DataTable
        value={loading ? skeletonRows : appointments}
        rowGroupMode="rowspan"
        paginator={appointments.length > 10}
        first={first}
        rows={rows}
        rowsPerPageOptions={[10, 20, 30]}
        onPage={onPageChange}
        className="datatable-right-align"
      >
        <Column
          field="position"
          header="Position"
          style={{ minWidth: "200px" }}
          body={(rowData) =>
            loading ? <Skeleton width="100%" /> : rowData?.position || "-"
          }
        />
        <Column
          field="role"
          header="Role"
          style={{ minWidth: "200px" }}
          body={(rowData) =>
            loading ? <Skeleton width="100%" /> : rowData?.role || "-"
          }
        />
        <Column
          field="start_date"
          header="Appointment Date"
          style={{ minWidth: "150px" }}
          body={(rowData) =>
            loading ? (
              <Skeleton width="100%" />
            ) : rowData?.start_date ? (
              format(rowData.start_date, "MMMM do, yyyy")
            ) : (
              "-"
            )
          }
        />
        <Column
          field="end_date"
          header="End Date"
          style={{ minWidth: "150px" }}
          body={(rowData) =>
            loading ? (
              <Skeleton width="100%" />
            ) : rowData?.end_date ? (
              format(rowData.end_date, "MMMM do, yyyy")
            ) : (
              "-"
            )
          }
        />
        <Column
          field="status"
          header="Status"
          style={{ minWidth: "150px" }}
          body={statusTemplate}
        />

        <Column
          className="table-col-width"
          body={(rowData) =>
            loading ? (
              <Skeleton width="100%" />
            ) : (
              <AdminOnly>
                <div className="flex align-items-center justify-content-end  gap-0 ">
                  <Menu menuItems={actionMenuItems(rowData)} />
                </div>
              </AdminOnly>
            )
          }
        />
      </DataTable>
    </div>
  );
};

export default AppointmentHistoryTable;
